import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldCheck } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { formatCount } from '@/utils/number';
import { PCMP_ACK_REMARKS_MAX } from '../../constants/pcmpDisburseVerify.constants';
import { useVerifyPcmpDisbursements } from '../../hooks/useVerifyPcmpDisbursements';
import type {
  PcmpDisburseVerifySelection,
  PcmpDisburseVerifyTab,
} from '../../types/pcmpDisburseVerify.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const verifyFormSchema = z.object({
  remarks: z.string().trim().max(PCMP_ACK_REMARKS_MAX),
});

type VerifyFormValues = z.infer<typeof verifyFormSchema>;

export interface PcmpVerifyDisbursementTarget {
  /** The Pending tab the proposals are verified from. */
  tab: PcmpDisburseVerifyTab;
  selection: PcmpDisburseVerifySelection;
  count: number;
  /** One proposal's ref no; left out for a list selection. */
  refNo?: string | undefined;
}

interface PcmpVerifyDisbursementDialogProps {
  target: PcmpVerifyDisbursementTarget;
  onVerified?: () => void;
  onClose: () => void;
}

/** Legacy DisburseVerifySubmit (LDS-770): one or many payouts; the remarks are optional. */
export function PcmpVerifyDisbursementDialog({
  target,
  onVerified,
  onClose,
}: PcmpVerifyDisbursementDialogProps) {
  const form = useForm<VerifyFormValues>({
    resolver: zodResolver(verifyFormSchema),
    defaultValues: { remarks: '' },
  });
  const { mutate, isPending, error } = useVerifyPcmpDisbursements(() => {
    onVerified?.();
    onClose();
  });

  const onSubmit = form.handleSubmit((values) =>
    mutate({ tab: target.tab, remarks: values.remarks || undefined, ...target.selection }),
  );

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Verify Disbursement"
        description={target.refNo ?? formatCount(target.count, 'proposal')}
        icon={ShieldCheck}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Verify"
        pendingLabel="Verifying..."
      >
        <p className="text-sm text-muted-foreground">
          {target.refNo
            ? 'Verify that this payout was made. A verification is final.'
            : `Verify the payouts of ${formatCount(target.count, 'proposal')}. A verification is final; any verified meanwhile are skipped.`}
        </p>
        <PcmpCommentField
          control={form.control}
          name="remarks"
          label="Remarks"
          placeholder="Optional"
          description="Recorded on each proposal's timeline."
          max={PCMP_ACK_REMARKS_MAX}
        />
      </PcmpEditDialog>
    </Form>
  );
}
